// 성능 최적화를 위한 커스텀 훅들
import { useRef, useCallback, useEffect } from 'react';

// 렌더링 최적화를 위한 훅 (슬라이딩 윈도우 기반)
export const useRenderOptimization = (componentName: string) => {
  const renderCount = useRef(0);
  const renderTimestamps = useRef<number[]>([]);

  renderCount.current += 1;
  const currentTime = Date.now();

  // 5초 슬라이딩 윈도우 내 렌더 횟수 추적
  renderTimestamps.current.push(currentTime);
  const WINDOW_MS = 5000;
  renderTimestamps.current = renderTimestamps.current.filter(
    t => currentTime - t < WINDOW_MS
  );

  const rendersInWindow = renderTimestamps.current.length;
  // 5초 내 10회 이상 → 실제 렌더 루프 의심
  const isExcessiveRendering = rendersInWindow > 10;
  // 5초 내 4회 이상의 빠른 연속 렌더
  const isFastRendering = rendersInWindow > 3 &&
    (currentTime - renderTimestamps.current[0]) < 1000;

  // 개발 모드에서만 경고
  if (process.env.NODE_ENV === 'development') {
    if (isExcessiveRendering && rendersInWindow % 5 === 0) {
      console.warn(`⚠️ [${componentName}] 과도한 렌더링 감지:`, {
        rendersInWindow,
        totalRenders: renderCount.current,
        windowMs: WINDOW_MS,
      });
    }
  }

  return {
    renderCount: renderCount.current,
    isExcessiveRendering,
    isFastRendering,
    resetRenderCount: useCallback(() => {
      renderCount.current = 0;
      renderTimestamps.current = [];
    }, [])
  };
};

// 월 변경 감지 최적화
export const useMonthChangeOptimization = (selectedMonth: string) => {
  const prevMonth = useRef<string>();
  const isMonthChanged = prevMonth.current !== selectedMonth;

  useEffect(() => {
    if (isMonthChanged) {
      prevMonth.current = selectedMonth;
    }
  }, [selectedMonth, isMonthChanged]);

  return {
    isMonthChanged,
    prevMonth: prevMonth.current
  };
};

// 조건부 useEffect (불필요한 실행 방지)
export const useConditionalEffect = (
  effect: React.EffectCallback,
  deps: React.DependencyList,
  condition: boolean
) => {
  useEffect(() => {
    if (condition) {
      return effect();
    }
  }, [...deps, condition]);
};
